import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { isEqual } from 'lodash';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import useGetConversationsQuery from '../queries/useGetConversationsQuery';

const ConversationFiltersContext = createContext();

export const useConversationFiltersContext = () => {
  const context = useContext(ConversationFiltersContext);
  if (!context) {
    throw new Error(
      'useConversationFiltersContext must be used within a ConversationFiltersProvider'
    );
  }
  return context;
};

// Default date range: Last 30 days
const getDefaultDateRange = () => {
  const start = moment()
    .subtract(3, 'months')
    .startOf('day')
    .format('YYYY-MM-DD');
  const end = moment().endOf('day').format('YYYY-MM-DD');
  return {
    start,
    end,
  };
};

const DEFAULT_FILTERS = {
  searchTerm: '',
  tags: [],
  markedAsNotHelpful: false,
  csatScore: undefined,
  includeProductionHubs: true,
  includeSuperadmin: false,
  includeClientAdmin: true,
  includeNonAdminUsers: true,
  raisedTicket: undefined,
  flaggedInternally: undefined,
  dateRange: getDefaultDateRange(),
};

const DEFAULT_PAGINATION = {
  page: 1,
  limit: 10,
};

// Helper functions to separate filters from pagination
const extractFilters = filters => {
  const { page, limit, ...filterValues } = filters;
  return filterValues;
};

export const ConversationFiltersProvider = ({ children }) => {
  const [conversationFilters, setConversationFilters] = useState({
    ...DEFAULT_FILTERS,
    ...DEFAULT_PAGINATION,
  });
  const [pendingFilters, setPendingFilters] = useState({
    ...DEFAULT_FILTERS,
    ...DEFAULT_PAGINATION,
  });
  const history = useHistory();

  const { refetch } = useGetConversationsQuery(conversationFilters);

  const updateFilters = useCallback((key, newValue) => {
    setPendingFilters(prev => {
      const updated = { ...prev, [key]: newValue };

      // Reset page to 1 when any filter other than page changes
      if (key !== 'page') {
        updated.page = 1;
      }

      return updated;
    });
  }, []);

  // Separate function for page changes that apply immediately
  const updatePage = useCallback(newPage => {
    setConversationFilters(prev => ({ ...prev, page: newPage }));
    setPendingFilters(prev => ({ ...prev, page: newPage }));
  }, []);

  const updateLimit = useCallback(newLimit => {
    setConversationFilters(prev => ({ ...prev, limit: newLimit }));
    setPendingFilters(prev => ({ ...prev, limit: newLimit }));
  }, []);

  const resetFilters = useCallback(() => {
    const currentFilters = extractFilters(pendingFilters);
    if (isEqual(currentFilters, DEFAULT_FILTERS)) {
      return;
    }
    setConversationFilters({ ...DEFAULT_FILTERS, ...DEFAULT_PAGINATION });
    setPendingFilters({ ...DEFAULT_FILTERS, ...DEFAULT_PAGINATION });
    history.push('/support-widget/conversations');
  }, [pendingFilters, history]);

  const applyFilters = useCallback(() => {
    setConversationFilters(pendingFilters);
    history.push('/support-widget/conversations');
  }, [pendingFilters, history]);

  const isDirty = useMemo(() => {
    const pendingFilterValues = extractFilters(pendingFilters);
    const appliedFilterValues = extractFilters(conversationFilters);

    return !isEqual(appliedFilterValues, pendingFilterValues);
  }, [conversationFilters, pendingFilters]);

  const enableClearAll = useMemo(() => {
    const pendingFilterValues = extractFilters(pendingFilters);
    return !isEqual(pendingFilterValues, DEFAULT_FILTERS);
  }, [pendingFilters]);

  // Only refetch when conversationFilters (applied filters) change
  useEffect(() => {
    const appliedFilterValues = extractFilters(conversationFilters);
    if (isEqual(appliedFilterValues, DEFAULT_FILTERS)) {
      refetch();
    }
  }, [conversationFilters, refetch]);

  const value = {
    conversationFilters,
    pendingFilters,
    isDirty,
    enableClearAll,
    setConversationFilters: updateFilters,
    updatePage,
    updateLimit,
    resetFilters,
    applyFilters,
  };

  return (
    <ConversationFiltersContext.Provider value={value}>
      {children}
    </ConversationFiltersContext.Provider>
  );
};
